import { useEffect, useState } from 'react';
import { useApp } from '../app/store.ts';
import { api } from '../data/api.ts';
import { store } from '../data/db.ts';
import { buildVaultZip, downloadZip } from '../lib/export.ts';
import { importArchive, type ImportProgress, type ImportSummary } from '../import/client.ts';
import { disablePasskey, enablePasskey, passkeyRegistered, signOut } from '../app/auth.ts';
import { passkeySupported } from '../crypto/passkey.ts';
import { applyUpdate, checkForUpdate } from '../app/updates.ts';
import { BUILD, serverFassung } from '../app/version.ts';
import {
  disablePush,
  enablePush,
  PushDenied,
  pushState,
  scheduleInfo,
  testPush,
  type PushState,
} from '../todos/push.ts';
import { funde as eingang, type Lage } from '../funde/store.ts';
import { Icon } from './Icon.tsx';
import type { BackupStatus } from '../../../shared/protocol.ts';

type Props = { onClose: () => void };

export function Settings({ onClose }: Props): JSX.Element {
  const { notes, folders, showTrash, setShowTrash, addFolder, renameFolder, recolorFolder, removeFolder, leave, lockAfter, setLockAfter, imageMode, setImageMode } =
    useApp();
  const [backup, setBackup] = useState<BackupStatus | null>(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupSlow, setBackupSlow] = useState(false);
  const [push, setPush] = useState<PushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushProblem, setPushProblem] = useState<string | null>(null);
  const [pushPlan, setPushPlan] = useState<{ count: number; next: number | null } | null>(null);
  const [standSucht, setStandSucht] = useState(false);
  const [stand, setStand] = useState<string | null>(null);
  const [server, setServer] = useState<string | null>(null);
  const [lage, setLage] = useState<Lage | null>(null);
  const [absender, setAbsender] = useState<
    { id: string; name: string; angelegt: number; zuletzt: number | null }[]
  >([]);
  const [frischerSchluessel, setFrischerSchluessel] = useState<string | null>(null);
  const [eingangArbeitet, setEingangArbeitet] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyProblem, setPasskeyProblem] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState('');
  const [lastExport, setLastExport] = useState<number | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    void api.backupStatus().then(setBackup).catch(() => setBackup(null));
    void passkeyRegistered().then(setHasPasskey);
    void pushState().then(setPush).catch(() => setPush(null));
    void scheduleInfo().then(setPushPlan).catch(() => setPushPlan(null));
    void store.getIndex().then((index) => setLastExport(index.lastExportAt));
    void eingang.lage().then(setLage).catch(() => setLage(null));
    void eingang
      .absender()
      .then((liste) => setAbsender(liste.absender))
      .catch(() => setAbsender([]));
  }, []);

  /**
   * Anstoßen und dann nachsehen, bis sich der Stand wirklich geändert hat.
   * Eine feste Wartezeit reicht nicht: Der Backup-Container prüft die Anstoßdatei
   * nur minütlich, und der Dump selbst braucht je nach Datenmenge noch einmal
   * seine Zeit. Vorher sähe der Stand unverändert aus — und mit ihm die Angabe
   * zur Offsite-Replikation.
   */
  async function triggerBackup(): Promise<void> {
    setBackupRunning(true);
    setBackupSlow(false);
    const before = backup?.finishedAt ?? null;
    try {
      await api.runBackup();
      for (let attempt = 0; attempt < 60; attempt += 1) {
        await new Promise((done) => setTimeout(done, 5_000));
        const current = await api.backupStatus().catch(() => null);
        if (!current) continue;
        setBackup(current);
        if (current.available && (current.finishedAt ?? null) !== before) return;
      }
      // Fünf Minuten ohne neuen Stand heißt nicht, dass nichts passiert —
      // nur, dass wir nicht länger warten.
      setBackupSlow(true);
    } catch {
      setBackupSlow(true);
    } finally {
      setBackupRunning(false);
    }
  }

  async function togglePush(on: boolean): Promise<void> {
    setPushBusy(true);
    setPushProblem(null);
    try {
      if (on) await enablePush();
      else await disablePush();
      setPush(await pushState());
      setPushPlan(await scheduleInfo().catch(() => null));
    } catch (error) {
      setPushProblem(
        error instanceof PushDenied
          ? 'Der Browser hat Mitteilungen abgelehnt. Das lässt sich nur in seinen Einstellungen zurücknehmen.'
          : 'Mitteilungen konnten nicht eingerichtet werden.',
      );
      setPush(await pushState().catch(() => null));
    } finally {
      setPushBusy(false);
    }
  }

  async function exportVault(): Promise<void> {
    const zip = await buildVaultZip(notes, folders);
    downloadZip(zip, `athene-export-${new Date().toISOString().slice(0, 10)}.zip`);
    const index = await store.getIndex();
    const now = Date.now();
    await store.setIndex({ ...index, lastExportAt: now });
    setLastExport(now);
  }

  const exportAge = lastExport === null ? null : (Date.now() - lastExport) / 86_400_000;

  return (
    <div className="panel">
      <header className="panel-head">
        <h2>Einstellungen</h2>
        <button className="head-icon" onClick={onClose} title="Schließen">
          <Icon name="close" />
        </button>
      </header>

      <div className="panel-body settings">
        <section>
          <h3>Ordner</h3>
          <p className="hint">Nur für größere Projekte. Alles andere lebt in der Liste.</p>
          <ul className="folder-list">
            {folders.map((folder) => (
              <li key={folder.id}>
                <input
                  value={folder.name}
                  onChange={(event) => void renameFolder(folder.id, event.target.value)}
                />
                <span className="swatches">
                  {[1, 2, 3, 4, 5, 0].map((tone) => (
                    <button
                      key={tone}
                      className={`swatch chip-${tone}${(folder.color ?? 0) === tone ? ' swatch-on' : ''}`}
                      title={`Farbe ${tone}`}
                      onClick={() => void recolorFolder(folder.id, tone)}
                    />
                  ))}
                </span>
                <button className="head-icon" title="Ordner entfernen" onClick={() => void removeFolder(folder.id)}>
                  <Icon name="trash" />
                </button>
              </li>
            ))}
          </ul>
          <div className="inline">
            <input
              placeholder="Neuer Ordner"
              value={newFolder}
              onChange={(event) => setNewFolder(event.target.value)}
            />
            <button
              className="ghost"
              disabled={!newFolder.trim()}
              onClick={() => {
                void addFolder(newFolder.trim());
                setNewFolder('');
              }}
            >
              Anlegen
            </button>
          </div>
        </section>

        <section>
          <h3>Versiegelter Eingang</h3>
          <p className="hint">
            Ein Einlieferer — etwa der Telegram-Bot — kann hier fertige Fundstücke
            ablegen, ohne irgendetwas lesen zu können. Er verschlüsselt für einen
            öffentlichen Schlüssel; aufmachen kann nur diese App. Der Server verwahrt
            solange einen Umschlag, den er selbst nicht öffnen kann.
          </p>

          {lage?.abweichung === true && (
            <p className="warn">
              Der Server kennt einen anderen Schlüssel als dieses Gerät. Wahrscheinlich
              wurde der Eingang anderswo neu eingerichtet — bis der Abgleich das hierher
              trägt, bleibt Eingeliefertes hier verschlossen.
            </p>
          )}

          <div className="inline">
            <button
              className="ghost"
              disabled={eingangArbeitet}
              onClick={() => {
                setEingangArbeitet(true);
                void eingang
                  .einrichten()
                  .then(() => eingang.lage().then(setLage))
                  .finally(() => setEingangArbeitet(false));
              }}
            >
              {lage?.bereit === true ? 'Neues Schlüsselpaar erzeugen' : 'Eingang einrichten'}
            </button>
            {lage?.bereit === true && (
              <button
                className="ghost"
                disabled={eingangArbeitet}
                onClick={() => {
                  setEingangArbeitet(true);
                  void eingang
                    .neuerAbsender('Telegram')
                    .then((neu) => {
                      setFrischerSchluessel(neu.token);
                      return eingang.absender().then((liste) => setAbsender(liste.absender));
                    })
                    .finally(() => setEingangArbeitet(false));
                }}
              >
                Einlieferungsschlüssel anlegen
              </button>
            )}
          </div>

          {lage?.bereit === true && (
            <p className="hint">
              Eingerichtet. Der öffentliche Schlüssel liegt beim Server; der Einlieferer
              holt ihn sich selbst.
            </p>
          )}

          {frischerSchluessel && (
            <>
              <p className="warn">
                Dieser Schlüssel wird jetzt einmal angezeigt und danach nie wieder —
                der Server verwahrt nur seine Prüfsumme.
              </p>
              <pre className="doc-text">{frischerSchluessel}</pre>
            </>
          )}

          {absender.length > 0 && (
            <ul className="rows">
              {absender.map((eintrag) => (
                <li className="row" key={eintrag.id}>
                  <span className="row-title">{eintrag.name || 'Ohne Namen'}</span>
                  <span className="row-sub">
                    {eintrag.zuletzt
                      ? `zuletzt ${new Date(eintrag.zuletzt).toLocaleString('de-DE')}`
                      : 'noch nie benutzt'}
                  </span>
                  <button
                    className="ghost"
                    onClick={() => {
                      void eingang
                        .absenderLoeschen(eintrag.id)
                        .then(() => eingang.absender().then((liste) => setAbsender(liste.absender)));
                    }}
                  >
                    Sperren
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3>Fassung</h3>
          <p className="hint">
            Dieses Gerät läuft auf dem Stand vom <strong>{BUILD}</strong>.
            {server !== null && (
              <>
                {' '}
                Der Server liefert <strong>{server}</strong> aus.
                {server === BUILD
                  ? ' Beides passt zusammen.'
                  : ' Dieses Gerät hält also eine ältere Fassung fest — „Jetzt aktualisieren" räumt sie weg.'}
              </>
            )}
          </p>
          <div className="inline">
            <button
              className="ghost"
              disabled={standSucht}
              onClick={() => {
                setStandSucht(true);
                setStand(null);
                // Zuerst den Server fragen: Fehlt dort schon der neue Stand, hilft
                // kein Aktualisieren auf dem Gerät, sondern nur ein neuer Bau.
                void serverFassung().then((fassung) => setServer(fassung?.gebaut ?? null));
                void checkForUpdate()
                  .then((neu) =>
                    setStand(neu ? 'Eine neue Fassung liegt bereit.' : 'Dies ist der neueste Stand.'),
                  )
                  .finally(() => setStandSucht(false));
              }}
            >
              {standSucht ? 'Wird gesucht …' : 'Nach Aktualisierung suchen'}
            </button>
            {stand?.startsWith('Eine neue') && (
              <button className="ghost" onClick={() => void applyUpdate()}>
                Jetzt aktualisieren
              </button>
            )}
          </div>
          {stand && <p className="hint">{stand}</p>}
        </section>

        <section>
          <h3>Mitteilungen</h3>
          <p className="hint">
            Fällige Erinnerungen melden sich, auch wenn die App geschlossen ist. Der
            Server erfährt dafür, <em>wann</em> etwas fällig ist — der Titel geht
            verschlüsselt mit und wird erst auf dem Gerät entziffert.
          </p>
          {push?.needsInstall && (
            <p className="warn">
              Auf dem iPhone gibt es Mitteilungen nur für die installierte App: im
              Teilen-Menü „Zum Home-Bildschirm“, und von dort aus einschalten.
            </p>
          )}
          {push && !push.supported && !push.needsInstall && (
            <p className="muted">Dieser Browser kann keine Mitteilungen anzeigen.</p>
          )}
          {push?.supported && !push.available && (
            <p className="muted">Der Server hat noch keine Push-Schlüssel erzeugt.</p>
          )}
          {push?.supported && push.available && (
            <div className="inline">
              <button className="ghost" disabled={pushBusy} onClick={() => void togglePush(!push.enabled)}>
                {pushBusy
                  ? 'Einen Moment …'
                  : push.enabled
                    ? 'Mitteilungen ausschalten'
                    : 'Mitteilungen einschalten'}
              </button>
              {push.enabled && (
                <button className="ghost" disabled={pushBusy} onClick={() => void testPush()}>
                  Probe schicken
                </button>
              )}
            </div>
          )}
          {pushProblem && <p className="warn">{pushProblem}</p>}
          {push?.enabled && (
            <p className="hint">
              {pushPlan
                ? `${pushPlan.count} Termin${pushPlan.count === 1 ? '' : 'e'} vorgemerkt${
                    pushPlan.next
                      ? `, der nächste am ${new Date(pushPlan.next).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ''
                  }. `
                : ''}
              Die App muss ab und zu geöffnet werden, damit weiter entfernte Termine
              nachrücken.
            </p>
          )}
        </section>

        <section>
          <h3>Sicherung auf dem Server</h3>
          {/* Ohne diesen Knopf ließe sich eine geänderte Offsite-Einstellung erst in
              der folgenden Nacht überprüfen. */}
          <div className="inline">
            <button
              className="ghost"
              disabled={backupRunning}
              onClick={() => void triggerBackup()}
            >
              {backupRunning ? 'Sicherung läuft …' : 'Jetzt sichern'}
            </button>
          </div>
          {backupSlow && (
            <p className="hint">
              Der Anstoß liegt im Datenverzeichnis; der Container hat ihn noch nicht
              abgearbeitet. Die Seite später noch einmal öffnen.
            </p>
          )}
          {backup === null && <p className="muted">Status nicht abrufbar.</p>}
          {backup && !backup.available && (
            <p className="warn">
              Noch kein Backup gelaufen. Der Backup-Container schreibt den ersten Stand
              beim Start und danach jede Nacht.
            </p>
          )}
          {backup?.available && (
            <>
              <p className={backup.ageHours !== null && backup.ageHours > 48 ? 'warn' : 'muted'}>
                {backup.ok === false
                  ? `Letzter Lauf fehlgeschlagen: ${backup.error ?? 'unbekannter Fehler'}`
                  : `Letzter Stand ${backup.finishedAt?.slice(0, 16).replace('T', ' ')} UTC, geprüft, ${
                      backup.sizeKiB ?? 0
                    } KiB.`}
              </p>
              {backup.ageHours !== null && backup.ageHours > 48 && backup.ok !== false && (
                <p className="warn">
                  Der letzte erfolgreiche Lauf ist über 48 Stunden her — bitte den
                  backup-Container prüfen.
                </p>
              )}
              <p className={backup.offsite === 'failed' ? 'warn' : 'hint'}>
                Offsite:{' '}
                {backup.offsite === 'ok'
                  ? 'repliziert'
                  : backup.offsite === 'failed'
                    ? `Replikation fehlgeschlagen${
                        backup.offsiteError ? ` — ${backup.offsiteError}` : ''
                      }`
                    : 'beim letzten Lauf war kein Ziel hinterlegt'}
              </p>
              {backup.offsite !== 'ok' && (
                <p className="hint">
                  {/* Die Angabe stammt aus dem letzten Lauf, nicht aus der aktuellen
                      Konfiguration. Nachgetragene RESTIC-Variablen wirken sich erst
                      mit dem nächsten Lauf aus — deshalb hier der Hinweis. */}
                  Nach dem Eintragen der RESTIC-Variablen einmal „Jetzt sichern“
                  drücken; vorher zeigt die Zeile den Stand von vor der Einrichtung.
                </p>
              )}
            </>
          )}
        </section>

        <section>
          <h3>Import</h3>
          <p className="hint">
            Obsidian-Vault oder Notion-Export als ZIP, oder einzelne Markdown-Dateien.
            Alles wird hier im Browser zerlegt und verschlüsselt — nichts davon geht
            unverschlüsselt zum Server.
          </p>
          {/* Das native Feld beschriftet sich selbst — auf Englisch, egal wie die Seite
              eingestellt ist. Deshalb versteckt und über ein eigenes Etikett bedient. */}
          <label className={progress !== null ? 'ghost file disabled' : 'ghost file'}>
            {progress !== null ? 'Import läuft …' : 'Datei auswählen'}
          <input
            type="file"
            accept=".zip,.md,.markdown,.txt"
            disabled={progress !== null}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setSummary(null);
              setImportError(null);
              setProgress({ done: 0, total: 0, step: 'Archiv wird gelesen' });
              void importArchive(file, setProgress)
                .then(setSummary)
                .catch((error: Error) => setImportError(error.message))
                .finally(() => {
                  setProgress(null);
                  event.target.value = '';
                });
            }}
          />
          </label>
          {progress && (
            <p className="muted">
              {progress.step}
              {progress.total > 0 && ` · ${progress.done} von ${progress.total}`}
            </p>
          )}
          {importError && <p className="error">{importError}</p>}
          {summary && (
            <p className="muted">
              {summary.source === 'notion' ? 'Notion-Export' : summary.source === 'obsidian' ? 'Obsidian-Vault' : 'Markdown'}{' '}
              übernommen: {summary.notes} {summary.notes === 1 ? 'Notiz' : 'Notizen'},{' '}
              {summary.attachments} {summary.attachments === 1 ? 'Anhang' : 'Anhänge'},{' '}
              {summary.folders} {summary.folders === 1 ? 'Ordner' : 'Ordner'}.
              {summary.skipped > 0 && ` ${summary.skipped} übersprungen.`}
              {summary.failed.length > 0 && ` ${summary.failed.length} fehlgeschlagen.`}
            </p>
          )}
        </section>

        <section>
          <h3>Eigener Export</h3>
          <p className="hint">
            ZIP mit echten Markdown-Dateien. Lesbar in Obsidian und in jedem Texteditor —
            die Versicherung dagegen, dass Athene irgendwann nicht mehr passt.
          </p>
          {exportAge !== null && exportAge > 90 && (
            <p className="warn">Der letzte Export ist {Math.round(exportAge)} Tage her.</p>
          )}
          <button className="ghost" onClick={() => void exportVault()}>Vault exportieren</button>
        </section>

        <section>
          <h3>Papierkorb</h3>
          <label className="check">
            <input
              type="checkbox"
              checked={showTrash}
              onChange={(event) => setShowTrash(event.target.checked)}
            />
            Gelöschte Notizen anzeigen
          </label>
        </section>

        <section>
          <h3>Bilder</h3>
          <p className="hint">
            Bildschirmfotos kommen meist als PNG und sind dadurch unnötig groß. Athene
            kodiert jedes Bild vor dem Verschlüsseln nach WebP um und behält nur, was
            wirklich kleiner ist — bei Bildschirmfotos sind das gut 50 Prozent, mit
            Verkleinern deutlich mehr. Animierte GIFs und SVG bleiben unangetastet.
          </p>
          <div className="inline">
            <select
              value={imageMode}
              onChange={(event) =>
                void setImageMode(event.target.value as 'small' | 'balanced' | 'original')
              }
            >
              <option value="small">Klein — höchstens 1600 px</option>
              <option value="balanced">Ausgewogen — höchstens 2560 px (Standard)</option>
              <option value="original">Original behalten — nichts anfassen</option>
            </select>
          </div>
          <p className="hint">
            Unabhängig davon liegt die Obergrenze je Datei bei 100 MB.
          </p>
        </section>

        <section>
          <h3>Entsperren per Passkey</h3>
          <p className="hint">
            Face ID, Fingerabdruck oder Geräte-PIN statt der Passphrase. Der Schlüssel
            wird dabei mit einem Geheimnis aus dem Passkey verpackt und liegt
            verschlüsselt auf diesem Gerät — der Server erfährt davon nichts.
          </p>
          <p className="hint">
            Der Preis: Wer dieses Gerät entsperrt in die Hand bekommt, kommt an die
            Notizen. Die Passphrase bleibt nötig, um neue Geräte einzurichten, und die
            24 Wörter bleiben die letzte Rettung.
          </p>
          {passkeyProblem && <p className="warn">{passkeyProblem}</p>}
          <div className="inline">
            {hasPasskey ? (
              <button
                className="ghost"
                onClick={() => {
                  void disablePasskey().then(() => setHasPasskey(false));
                }}
              >
                Passkey entfernen
              </button>
            ) : (
              <button
                className="ghost"
                disabled={passkeyBusy || !passkeySupported()}
                onClick={() => {
                  setPasskeyProblem(null);
                  setPasskeyBusy(true);
                  void enablePasskey()
                    .then(() => setHasPasskey(true))
                    .catch((caught: Error) => setPasskeyProblem(caught.message))
                    .finally(() => setPasskeyBusy(false));
                }}
              >
                {passkeyBusy ? 'Wird eingerichtet …' : 'Passkey einrichten'}
              </button>
            )}
          </div>
          {!passkeySupported() && (
            <p className="hint">Dieser Browser kann keine Passkeys.</p>
          )}
        </section>

        <section>
          <h3>Automatisch sperren</h3>
          <p className="hint">
            Nach Untätigkeit vergisst die App den Schlüssel im Arbeitsspeicher. Der
            lokale Zwischenspeicher bleibt und ist erst nach erneuter Eingabe der
            Passphrase wieder lesbar.
          </p>
          <div className="inline">
            <select
              className="lock-after"
              value={String(lockAfter)}
              onChange={(event) => void setLockAfter(Number(event.target.value))}
            >
              <option value="5">nach 5 Minuten</option>
              <option value="15">nach 15 Minuten</option>
              <option value="30">nach 30 Minuten</option>
              <option value="60">nach einer Stunde</option>
              <option value="0">nie</option>
            </select>
          </div>
        </section>

        <section>
          <h3>Sicherheit</h3>
          <div className="inline">
            <button className="ghost" onClick={() => leave()}>Sperren</button>
            <button
              className="ghost"
              onClick={() => {
                void signOut().then(() => window.location.reload());
              }}
            >
              Abmelden und lokalen Cache löschen
            </button>
          </div>
          <p className="hint">
            Sperren vergisst den Schlüssel im Arbeitsspeicher. Abmelden entfernt
            zusätzlich den entschlüsselten Zwischenspeicher von diesem Gerät.
          </p>
        </section>
      </div>
    </div>
  );
}
